import { observer } from 'mobx-react-lite';
import * as React from 'react';
import Ribbon from './Ribbon';
import type RibbonPlugin from '../plugin/RibbonPlugin';
import type RibbonViewState from '../store/schema/RibbonViewState';
import fontName from './buttons/fontName';
import fontSize from './buttons/fontSize';
import FontNameWithInput from './buttons/FontNameWithInput';
import FontSizeWithInput from './buttons/FontSizeWithInput';
import bold from './buttons/bold';
import italic from './buttons/italic';
import underline from './buttons/underline';
import backColor, { splittedBackColor } from './buttons/backColor';
import textColor, { splittedTextColor } from './buttons/textColor';
import setContextualFormatBarTarget from '../actions/setContextualFormatBarTarget';
import type { PluginUIProps } from 'owa-editor/lib/store/schema/PluginWithUI';
import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isCurrentCultureRightToLeft } from 'owa-localize';

const styles = require('./ContextualFormatBar.scss');

export default observer(function ContextualFormatBar(
    props: PluginUIProps<RibbonViewState, RibbonPlugin>
) {
    const formatBarDiv = React.useRef<HTMLElement>();
    const suspendDismissOnce = React.useRef<boolean>();
    const useFontPickerWithInput = isFeatureEnabled('cmp-font-search');
    const useColorSplitButton = isFeatureEnabled('cmp-color-splitButton');
    const buttons = React.useMemo(
        () => [
            useFontPickerWithInput ? new FontNameWithInput() : fontName,
            useFontPickerWithInput ? new FontSizeWithInput() : fontSize,
            bold,
            italic,
            underline,
            useColorSplitButton ? splittedBackColor : backColor,
            useColorSplitButton ? splittedTextColor : textColor,
        ],
        [props, useFontPickerWithInput, useColorSplitButton]
    );
    const onDismiss = (evt: any) => {
        // Do not dismiss if clicking on buttons inside contextual format bar
        let target =
            evt instanceof FocusEvent && evt.relatedTarget instanceof Node
                ? evt.relatedTarget
                : null;
        let clickOnRibbon = target && formatBarDiv.current && formatBarDiv.current.contains(target);
        if (!clickOnRibbon && !suspendDismissOnce.current) {
            setContextualFormatBarTarget(props.viewState, null /*targetPoint*/);
        }
        suspendDismissOnce.current = false;
    };
    const onDropDownDismissed = () => {
        suspendDismissOnce.current = true;
    };
    let { plugin, viewState, editorViewState } = props;
    let { targetPoint } = viewState;
    return (
        targetPoint && (
            <Callout
                gapSpace={10}
                isBeakVisible={false}
                target={targetPoint}
                directionalHint={DirectionalHint.topLeftEdge}
                directionalHintForRTL={DirectionalHint.topRightEdge}
                onDismiss={onDismiss}>
                <div ref={ref => (formatBarDiv.current = ref)}>
                    <Ribbon
                        className={styles.contextualFormatBar}
                        editorViewState={editorViewState}
                        viewState={viewState}
                        skipInitialResize={true}
                        isDarkMode={plugin.isDarkMode()}
                        buttons={buttons}
                        onDropDownDismissed={onDropDownDismissed}
                        focusToButtonBeforeDropDown={true}
                        isRtl={isCurrentCultureRightToLeft()}
                    />
                </div>
            </Callout>
        )
    );
});
